'use client';

import { Loader2, LocateFixed, MapPin, Search } from 'lucide-react';
import { useLocale, useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import { useCallback, useEffect, useId, useRef, useState } from 'react';

import {
  KeywordSuggestionsDropdown,
  MAX_KEYWORD_QUERY_LENGTH,
  MIN_KEYWORD_QUERY_LENGTH,
  type SuggestionItem,
  buildSuggestionItems,
  suggestionOptionId,
  useSuggestionNavigation
} from '@website/components/KeywordSuggestionsDropdown';
import type { RecentLocation } from '@website/components/LocationPicker/types';
import { useLocationSearch } from '@website/hooks/useLocationSearch';
import { useSuggestions } from '@website/hooks/useSuggestions';
import { locationService } from '@website/services/location.service';

export interface HomeSearchBarLocation {
  latitude: number;
  longitude: number;
  name: string;
}

interface HomeSearchBarProps {
  className?: string;
  onSearch: (query: string, location?: HomeSearchBarLocation) => void;
  /**
   * Pre-resolved location (e.g. the user's reverse-geocoded city). Unlike a
   * plain label, this carries coordinates so an untouched prefill is actually
   * submitted with the search. Hand-typed text that never resolves to a
   * geocoding result is still dropped on submit (best-effort, never
   * wrong-coordinates).
   */
  initialLocation?: HomeSearchBarLocation;
}

function toRecentLocation(location: HomeSearchBarLocation): RecentLocation {
  return {
    id: `initial:${location.name}`,
    name: location.name,
    description: location.name,
    latitude: location.latitude,
    longitude: location.longitude
  };
}

export function HomeSearchBar({ className, onSearch, initialLocation }: HomeSearchBarProps) {
  const t = useTranslations();
  const locale = useLocale();
  const router = useRouter();
  const listboxId = useId();
  const [keyword, setKeyword] = useState('');
  const [debouncedKeyword, setDebouncedKeyword] = useState('');
  const [locationInput, setLocationInput] = useState(initialLocation?.name || '');
  const [showDropdown, setShowDropdown] = useState(false);
  const [showKeywordDropdown, setShowKeywordDropdown] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const selectedLocationRef = useRef<RecentLocation | null>(initialLocation ? toRecentLocation(initialLocation) : null);
  // Once the user edits the location field, a late-arriving initialLocation
  // (geolocation resolves asynchronously) must not overwrite their input.
  const userEditedLocationRef = useRef(false);
  const setSelectedLocation = (loc: RecentLocation | null) => {
    selectedLocationRef.current = loc;
  };

  useEffect(() => {
    const id = setTimeout(() => setDebouncedKeyword(keyword), 150);
    return () => clearTimeout(id);
  }, [keyword]);

  // Fetch only while the dropdown can show the results — otherwise picking a
  // suggestion would fire one last wasted request as the router navigates away.
  const { data: keywordSuggestions = [], isFetching: isFetchingSuggestions } = useSuggestions(
    showKeywordDropdown ? debouncedKeyword : '',
    locale
  );

  const {
    searchQuery,
    setSearchQuery,
    searchResults,
    searchResultsQuery,
    isSearching,
    recentLocations,
    saveRecentLocation,
    getCurrentLocation,
    isLoadingCurrentLocation
  } = useLocationSearch();

  // Keyed on the location's content, not object identity — the parent builds
  // this prop inline, so identity changes on every render.
  useEffect(() => {
    if (initialLocation && !userEditedLocationRef.current) {
      setLocationInput(initialLocation.name);
      setSelectedLocation(toRecentLocation(initialLocation));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialLocation?.name, initialLocation?.latitude, initialLocation?.longitude]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
        setShowKeywordDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLocationInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    userEditedLocationRef.current = true;
    setLocationInput(value);
    setSearchQuery(value);
    setSelectedLocation(null);
    setShowDropdown(true);
    setShowKeywordDropdown(false);
  };

  const handleLocationSelect = useCallback(
    (location: RecentLocation) => {
      userEditedLocationRef.current = true;
      setLocationInput(location.name);
      setSelectedLocation(location);
      saveRecentLocation(location);
      setShowDropdown(false);
    },
    [saveRecentLocation]
  );

  const handleUseCurrentLocation = useCallback(async () => {
    const location = await getCurrentLocation();
    if (location) {
      userEditedLocationRef.current = true;
      setLocationInput(location.name);
      setSelectedLocation(location);
      setShowDropdown(false);
    }
  }, [getCurrentLocation]);

  /**
   * Resolve the location the search should carry:
   * 1. an explicitly selected (or prefilled) location,
   * 2. otherwise, if the user typed location text without picking from the
   *    dropdown, the best (first) geocoding match — but only when the results
   *    actually belong to the text currently in the field (debounce/in-flight
   *    results belong to an older keystroke and must not be trusted),
   * 3. otherwise no location (typed text that never resolved is dropped —
   *    best-effort, never wrong-coordinates).
   */
  const resolveSubmittedLocation = useCallback(async (): Promise<HomeSearchBarLocation | undefined> => {
    const selected = selectedLocationRef.current;
    if (selected && locationInput.trim()) {
      return { latitude: selected.latitude, longitude: selected.longitude, name: selected.name };
    }
    const trimmed = locationInput.trim();
    if (!trimmed) return undefined;
    const resultsMatchInput = !isSearching && searchResultsQuery.trim() === trimmed;
    if (resultsMatchInput && searchResults.length > 0) {
      const best = searchResults[0]!;
      return { latitude: best.latitude, longitude: best.longitude, name: best.name };
    }
    // The user typed a location but the debounced geocode hasn't caught up (they
    // hit search before it resolved). Resolve it synchronously here so the typed
    // city is honored — otherwise we'd submit with no location and the search
    // page silently restores the last stored location (e.g. a previous Paris
    // search), ignoring what the user typed.
    try {
      const [best] = await locationService.searchLocations(trimmed);
      if (best) return { latitude: best.latitude, longitude: best.longitude, name: best.name };
    } catch (error) {
      console.error('[HomeSearchBar] Location resolve on submit failed:', error);
    }
    return undefined;
  }, [locationInput, searchResults, searchResultsQuery, isSearching]);

  const submitSearch = useCallback(
    async (keywordValue: string) => {
      // Below the searchable minimum the API ignores the keyword entirely —
      // don't put a no-op q= in the URL (the dropdown hint explains why).
      const keyword = keywordValue.trim().length >= MIN_KEYWORD_QUERY_LENGTH ? keywordValue.trim() : '';
      if (!keyword && keywordValue.trim()) {
        // The typed keyword is below the searchable minimum: surface the
        // min-length hint instead of silently discarding their text — even
        // when a location-only search would be possible. Clearing the field
        // lets them search by location alone.
        setShowKeywordDropdown(true);
        return;
      }
      const loc = await resolveSubmittedLocation();
      if (!keyword && !loc) return;
      setShowDropdown(false);
      setShowKeywordDropdown(false);
      onSearch(keyword, loc);
    },
    [onSearch, resolveSubmittedLocation]
  );

  const handleSubmit = () => void submitSearch(keyword);

  const handleKeywordSelect = useCallback(
    (item: SuggestionItem) => {
      setShowKeywordDropdown(false);
      // An exact activity pick is a navigation, not a search: deep-link to the
      // activity page so the pick can never dead-end on the search page's
      // location filter (suggestions are global, results are location-scoped).
      if (item.type === 'activity' && item.id) {
        router.push(`/${locale}/activity/${item.id}`);
        return;
      }
      setKeyword(item.name);
      void submitSearch(item.name);
    },
    [submitSearch, router, locale]
  );

  const keywordDropdownOpen = showKeywordDropdown && keyword.trim().length > 0;
  const showMinLengthHint = keyword.trim().length > 0 && keyword.trim().length < MIN_KEYWORD_QUERY_LENGTH;
  // While the hint is up and a refetch is in flight, placeholderData would
  // show the PREVIOUS query's suggestions under the hint — suppress them.
  const suggestionItems = buildSuggestionItems(
    keyword,
    showMinLengthHint && isFetchingSuggestions ? [] : keywordSuggestions
  );

  const { highlightedIndex, handleKeyDown: handleKeywordKeyDown } = useSuggestionNavigation({
    items: suggestionItems,
    isOpen: keywordDropdownOpen,
    open: () => setShowKeywordDropdown(true),
    close: () => setShowKeywordDropdown(false),
    onPick: handleKeywordSelect,
    onSubmit: handleSubmit,
    resetKey: keyword
  });

  // The mobile and desktop layouts each render their own combobox instance,
  // so each needs its own listbox id to keep DOM ids unique.
  const keywordInputProps = (variant: 'mobile' | 'desktop') => {
    const id = `${listboxId}-${variant}`;
    return {
      type: 'text' as const,
      role: 'combobox' as const,
      'aria-expanded': keywordDropdownOpen,
      // Only reference the listbox while it exists in the DOM.
      'aria-controls': keywordDropdownOpen ? id : undefined,
      'aria-autocomplete': 'list' as const,
      'aria-activedescendant': highlightedIndex >= 0 ? suggestionOptionId(id, highlightedIndex) : undefined,
      'aria-label': t('SearchPage.searchPlaceholder'),
      maxLength: MAX_KEYWORD_QUERY_LENGTH,
      placeholder: t('SearchPage.searchPlaceholder'),
      value: keyword,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
        setKeyword(e.target.value);
        setShowKeywordDropdown(true);
      },
      onFocus: () => {
        setShowKeywordDropdown(true);
        setShowDropdown(false);
      },
      onKeyDown: handleKeywordKeyDown
    };
  };

  const keywordDropdown = (variant: 'mobile' | 'desktop') =>
    keywordDropdownOpen && (
      <KeywordSuggestionsDropdown
        listboxId={`${listboxId}-${variant}`}
        items={suggestionItems}
        highlightedIndex={highlightedIndex}
        isLoading={isFetchingSuggestions}
        onSelect={handleKeywordSelect}
        showMinLengthHint={showMinLengthHint}
      />
    );

  // Determine dropdown items
  const hasQuery = searchQuery.trim().length >= 2;
  const suggestions = hasQuery ? searchResults : recentLocations;
  // Navigable location options: "use current location" first, then the
  // geocode results / recents (hidden while a search is in flight).
  const locationOptions: Array<RecentLocation | 'current'> = ['current', ...(isSearching ? [] : suggestions)];

  const pickLocationOption = useCallback(
    (option: RecentLocation | 'current') => {
      if (option === 'current') {
        void handleUseCurrentLocation();
      } else {
        handleLocationSelect(option);
      }
    },
    [handleUseCurrentLocation, handleLocationSelect]
  );

  const [locationHighlight, setLocationHighlight] = useState(-1);
  useEffect(() => {
    if (!showDropdown) setLocationHighlight(-1);
  }, [showDropdown]);
  useEffect(() => {
    setLocationHighlight(-1);
  }, [locationInput, locationOptions.length]);

  const handleLocationKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.nativeEvent.isComposing) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!showDropdown) setShowDropdown(true);
      setLocationHighlight((i) => Math.min(i + 1, locationOptions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setLocationHighlight((i) => Math.max(i - 1, -1));
    } else if (e.key === 'Enter') {
      const highlighted = showDropdown && locationHighlight >= 0 ? locationOptions[locationHighlight] : undefined;
      if (highlighted) {
        pickLocationOption(highlighted);
      } else {
        handleSubmit();
      }
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
    }
  };

  // Same combobox pattern as the keyword input; per-variant ids because the
  // mobile and desktop layouts each mount their own instance.
  const locationInputProps = (variant: 'mobile' | 'desktop') => {
    const id = `${listboxId}-location-${variant}`;
    return {
      type: 'text' as const,
      role: 'combobox' as const,
      'aria-expanded': showDropdown,
      'aria-controls': showDropdown ? id : undefined,
      'aria-autocomplete': 'list' as const,
      'aria-activedescendant': locationHighlight >= 0 ? suggestionOptionId(id, locationHighlight) : undefined,
      'aria-label': t('SearchPage.chooseLocation'),
      placeholder: t('SearchPage.chooseLocation'),
      value: locationInput,
      onChange: handleLocationInputChange,
      onFocus: () => {
        setShowDropdown(true);
        setShowKeywordDropdown(false);
      },
      onKeyDown: handleLocationKeyDown
    };
  };

  const locationDropdown = (variant: 'mobile' | 'desktop') => {
    if (!showDropdown) return null;
    const id = `${listboxId}-location-${variant}`;
    return (
      <div className="absolute top-full left-0 right-0 md:w-[320px] mt-1 bg-white border border-neutral-200 rounded-lg shadow-lg z-50 max-h-[280px] overflow-y-auto">
        <div role="listbox" id={id} aria-label={t('SearchPage.chooseLocation')}>
          {locationOptions.map((option, index) => {
            const highlighted = index === locationHighlight;
            if (option === 'current') {
              return (
                <button
                  key="current"
                  id={suggestionOptionId(id, index)}
                  role="option"
                  aria-selected={highlighted}
                  type="button"
                  tabIndex={-1}
                  className={`w-full flex items-center gap-2 px-4 py-2.5 transition-colors text-left border-b border-neutral-100 ${
                    highlighted ? 'bg-neutral-100' : 'hover:bg-neutral-50'
                  }`}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => pickLocationOption(option)}
                  disabled={isLoadingCurrentLocation}
                >
                  {isLoadingCurrentLocation ? (
                    <Loader2 className="w-4 h-4 text-primary-500 shrink-0 animate-spin" />
                  ) : (
                    <LocateFixed className="w-4 h-4 text-primary-500 shrink-0" />
                  )}
                  <span className="text-sm font-medium text-primary-500">{t('SearchPage.useCurrentLocation')}</span>
                </button>
              );
            }
            return (
              <button
                key={option.id}
                id={suggestionOptionId(id, index)}
                role="option"
                aria-selected={highlighted}
                type="button"
                tabIndex={-1}
                className={`w-full flex items-start gap-2 px-4 py-2.5 transition-colors text-left ${
                  highlighted ? 'bg-neutral-100' : 'hover:bg-neutral-50'
                }`}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pickLocationOption(option)}
              >
                <MapPin className="w-4 h-4 text-neutral-400 mt-0.5 shrink-0" />
                <div className="flex flex-col min-w-0">
                  <span className="text-sm font-medium text-neutral-800 truncate">{option.name}</span>
                  {option.description !== option.name && (
                    <span className="text-xs text-neutral-500 truncate">{option.description}</span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
        {isSearching ? (
          <div className="px-4 py-3 text-sm text-neutral-500" role="status">
            {t('SearchPage.searching')}
          </div>
        ) : suggestions.length === 0 && hasQuery ? (
          <div className="px-4 py-3 text-sm text-neutral-500">{t('SearchPage.noLocationsFound')}</div>
        ) : null}
      </div>
    );
  };

  return (
    // z-40: must out-stack the hero collage's decorative labels (z-30 in
    // VisualSection) or they bleed through the suggestions dropdown — the
    // dropdown's own z-50 is capped by this container's stacking context.
    <div ref={containerRef} className={`relative z-40 w-full max-w-[576px] ${className || ''}`}>
      {/* Mobile: stacked vertical layout */}
      <div className="flex flex-col gap-3 md:hidden">
        <div className="relative">
          <div className="flex items-center gap-2 p-3 rounded-lg border border-neutral-200 bg-white transition-colors hover:border-primary-500 focus-within:border-primary-500">
            <MapPin className="w-6 h-6 text-neutral-600 shrink-0" />
            <input
              {...locationInputProps('mobile')}
              className="bg-transparent outline-none text-base font-medium text-neutral-600 w-full placeholder:text-neutral-600"
            />
          </div>
          {locationDropdown('mobile')}
        </div>

        <div className="relative">
          <div className="flex items-center gap-2 p-3 rounded-lg border border-neutral-200 bg-white transition-colors hover:border-primary-500 focus-within:border-primary-500">
            <Search className="w-6 h-6 text-neutral-600 shrink-0" />
            <input
              {...keywordInputProps('mobile')}
              className="bg-transparent outline-none text-base font-medium text-neutral-600 w-full placeholder:text-neutral-600"
            />
            <button
              type="button"
              onClick={handleSubmit}
              aria-label={t('SearchPage.searchPlaceholder')}
              className="flex items-center justify-center w-9 h-9 rounded-md bg-primary-500 hover:bg-primary-600 transition-colors shrink-0"
            >
              <Search className="w-4 h-4 text-white" />
            </button>
          </div>
          {keywordDropdown('mobile')}
        </div>
      </div>

      {/* Desktop: single unified container */}
      <div className="hidden md:flex items-center h-[46px] rounded-lg border border-neutral-200 bg-white transition-colors hover:border-primary-500 focus-within:border-primary-500">
        <div className="relative flex items-center gap-2 px-3 shrink-0 w-[200px] h-full">
          <MapPin className="w-5 h-5 text-neutral-600 shrink-0" />
          <input
            {...locationInputProps('desktop')}
            className="bg-transparent outline-none text-sm font-medium text-neutral-600 w-full placeholder:text-neutral-600"
          />
          {locationDropdown('desktop')}
        </div>

        <div className="w-px h-5 bg-neutral-300 shrink-0" />

        <div className="relative flex items-center gap-2 px-3 flex-1 h-full">
          <Search className="w-4 h-4 text-neutral-600 shrink-0" />
          <input
            {...keywordInputProps('desktop')}
            className="bg-transparent outline-none text-sm font-medium text-neutral-600 w-full placeholder:text-neutral-600"
          />
          {keywordDropdown('desktop')}
        </div>

        <button
          type="button"
          onClick={handleSubmit}
          aria-label={t('SearchPage.searchPlaceholder')}
          className="flex items-center justify-center w-9 h-9 mr-1.5 rounded-md bg-primary-500 hover:bg-primary-600 transition-colors shrink-0"
        >
          <Search className="w-4 h-4 text-white" />
        </button>
      </div>
    </div>
  );
}
